import { useEffect, useState, type ReactNode } from 'react';
import { Link, Redirect, useLocation } from 'wouter';
import { toast } from 'sonner';
import { Logo } from './Logo';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';
import { Footer } from './Footer';
import { ScrollableTabStrip } from './ScrollableTabStrip';
import { api } from '../lib/api';
import type { AppUser } from '../types';
import { cn } from '../lib/utils';
import { LAYOUT } from '../styles/layout-tokens';

// Admin surface uses hardcoded English strings. It is operator-only and adding
// translations is deferred until the feature stabilises.

interface AdminLayoutProps {
  children: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const [user, setUser] = useState<AppUser | null | undefined>(undefined);
  const [location] = useLocation();

  useEffect(() => {
    api.getMe().then(setUser).catch(() => setUser(null));
  }, []);

  const handleLogout = async () => {
    try {
      await api.logout();
      window.location.href = '/';
    } catch {
      toast.error('Logout failed');
    }
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <div className="text-sm text-zinc-500">Loading…</div>
      </div>
    );
  }

  if (!user) return <Redirect to="/" replace />;
  if (user.role !== 'admin') return <Redirect to="/" replace />;

  return (
    <div className="min-h-screen flex flex-col bg-zinc-50 dark:bg-zinc-950">
      <header className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className={cn(LAYOUT.container.wide, 'mx-auto', LAYOUT.page.paddingX, LAYOUT.header.height, 'flex items-center justify-between gap-3')}>
          <Link href="/home" className="flex items-center gap-3">
            <Logo className="h-7 w-auto" />
            <span className="font-bold text-lg tracking-tight brand-text">
              MarkFlow
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500 border border-zinc-300 dark:border-zinc-700 rounded px-1.5 py-0.5">
              Admin
            </span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            <UserMenu user={user} onLogout={handleLogout} />
          </div>
        </div>
        <ScrollableTabStrip>
          <AdminTab
            href="/admin/providers"
            active={location.startsWith("/admin/providers")}
          >
            AI Providers
          </AdminTab>
          <AdminTab
            href="/admin/users"
            active={location.startsWith("/admin/users")}
          >
            Users
          </AdminTab>
          <AdminTab
            href="/admin/rate-limits"
            active={location.startsWith("/admin/rate-limits")}
          >
            Rate limits
          </AdminTab>
          <AdminTab
            href="/admin/request-limits"
            active={location.startsWith("/admin/request-limits")}
          >
            Request limits
          </AdminTab>
          <AdminTab
            href="/admin/anomaly"
            active={location.startsWith("/admin/anomaly")}
          >
            Anomaly
          </AdminTab>
          <AdminTab
            href="/admin/alerts"
            active={location.startsWith("/admin/alerts")}
          >
            Alerts
          </AdminTab>
          <AdminTab
            href="/admin/catalog"
            active={location.startsWith("/admin/catalog")}
          >
            Catalog
          </AdminTab>
          <AdminTab
            href="/admin/ai-queues"
            active={location.startsWith("/admin/ai-queues")}
          >
            AI Queues
          </AdminTab>
          <AdminTab
            href="/admin/categorization"
            active={location.startsWith("/admin/categorization")}
          >
            Categorization
          </AdminTab>
          <AdminTab
            href="/admin/chat-costs"
            active={location.startsWith("/admin/chat-costs")}
          >
            Chat costs
          </AdminTab>
          <AdminTab
            href="/admin/features"
            active={location.startsWith("/admin/features")}
          >
            Features
          </AdminTab>
          <AdminTab
            href="/admin/backups"
            active={location.startsWith("/admin/backups")}
          >
            Backups
          </AdminTab>
        </ScrollableTabStrip>
      </header>

      <main className="flex-1">
        <div className={cn(LAYOUT.container.default, 'mx-auto', LAYOUT.page.paddingX, LAYOUT.page.paddingY)}>
          {children}
        </div>
      </main>

      <Footer />
    </div>
  );
}

function AdminTab({
  href,
  active,
  children,
}: {
  href: string;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <Link
      href={href}
      className={cn(
        'inline-flex items-center px-3 py-2 text-sm font-medium border-b-2 transition-colors shrink-0',
        active
          ? 'border-violet-500 text-violet-700 dark:text-violet-300'
          : 'border-transparent text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-zinc-300 dark:hover:border-zinc-700',
      )}
    >
      {children}
    </Link>
  );
}
